import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { useTheme } from '@mui/material/styles';
import { Send } from 'react-feather';

const ChatInput = React.memo(function ChatInput({ inputRef, onSend, onKeyDown, isLoading, isLoadingSession, sessionId }) {
  const theme = useTheme();
  const [hasContent, setHasContent] = useState(false);
  
  // Helper to check content
  const checkContent = React.useCallback((value) => {
    const trimmed = (value || '').trim();
    setHasContent(trimmed.length > 0);
  }, []);

  return (
    <Box
      sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2, 3),
        backgroundColor: theme.palette.background.paper
      }}
    >
      <TextField
        inputRef={inputRef}
        fullWidth
        multiline
        maxRows={4}
        placeholder="How can RiseAI help?"
        onKeyDown={onKeyDown}
        onKeyUp={(e) => {
          // Update hasContent state only on keyup (less frequent than onChange)
          checkContent(e.target.value);
        }}
        onFocus={(e) => {
          // Check content on focus (also fires when value is set programmatically and focused)
          checkContent(e.target.value);
        }}
        onInput={(e) => {
          // Also check on input for immediate feedback (but this is still less frequent than controlled onChange)
          checkContent(e.target.value);
        }}
        disabled={isLoading || isLoadingSession || !sessionId}
        variant="outlined"
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                color="primary"
                // Wrap onSend so we don't pass the click event as the first argument.
                // handleSendMessage expects an optional boolean (skipUserMessage),
                // and getting the event there prevents the user message from being added/cleared.
                onClick={() => onSend()}
                disabled={!hasContent || isLoading || isLoadingSession || !sessionId}
                sx={{
                  backgroundColor: theme.palette.riseAiPurple,
                  color: theme.palette.common.white,
                  '&:hover': {
                    backgroundColor: theme.palette.riseAiPurple,
                    opacity: 0.9
                  },
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.action.disabledBackground
                  }
                }}
              >
                <Send size={18} />
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.default,
            padding: theme.spacing(2),
            borderColor: theme.palette.text.border,
            borderRadius: theme.shape.borderRadius * 0.5,
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.text.border
            }
          }
        }}
      />
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 1, display: 'block', textAlign: 'center' }}>
        Check AI content for accuracy.
      </Typography>
    </Box>
  );
});

export default ChatInput;
